import React, { useEffect, useState } from 'react';
import AgyleDictionaryField from '../../../../../../components/ui/selector/agyleDictionaryField';
import { useDebounce } from 'use-debounce';
import { isEmpty } from 'lodash';
import { prescriptionTerminologyApi } from '../../../../../../app/apiPrescription';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MedicationIcon from '@mui/icons-material/Medication';

const MedicineAutocomplete = ({ value, handleChangeValue, disabled, error = false }) => {
    const { t } = useTranslation();
    const controller = new AbortController();

    // Input states
    const [autocompleteOpen, setAutocompleteOpen] = useState(false);
    const [autocompleteOptions, setAutocompleteOptions] = useState([]);
    const [autocompleteInputValue, setAutocompleteInputValue] = useState('');
    const [autocompleteLoading, setAutocompleteLoading] = useState(false);

    // debounced option
    const [autocompleteInputValueDebounced] = useDebounce(autocompleteInputValue, 250);

    // Value
    const [inputValue, setInputValue] = useState(null);

    useEffect(() => {
        return () => {
            controller.abort();
        };
    }, []);

    // Search
    useEffect(() => {
        if (autocompleteInputValueDebounced && !isEmpty(autocompleteInputValueDebounced) && autocompleteInputValueDebounced?.length > 2) {
            setAutocompleteLoading(true);
            setAutocompleteOpen(true);

            prescriptionTerminologyApi
                .searchMedicine({ searchTerm: autocompleteInputValueDebounced, signal: controller.signal })
                .then((result) => {
                    if (controller.signal.aborted) return;

                    if (result?.medicines) {
                        setAutocompleteOptions(result.medicines);
                    } else {
                        setAutocompleteOptions([]);
                    }
                    setAutocompleteLoading(false);
                })
                .catch(function (error) {
                    console.error('Autocomplete dictionaries error', error);
                    if (!controller.signal.aborted) {
                        setAutocompleteOptions([]);
                        setAutocompleteLoading(false);
                    }
                });
        } else {
            // Resetuj opcje gdy input jest za krótki
            setAutocompleteOptions([]);
            setAutocompleteLoading(false);
        }

        return () => {
            controller.abort();
        };
    }, [autocompleteInputValueDebounced]);

    // Prepopulate value
    useEffect(() => {
        setInputValue(value ?? null);
    }, [value]);

    const handleAutocompleteState = (val) => {
        setAutocompleteOpen(val);
    };

    const handleChange = (_, newValues) => {
        // Prevent changes if form is disabled
        if (disabled) return;

        handleChangeValue(newValues);
        setAutocompleteOpen(false);
    };

    return (
        <div style={{ width: '100%' }}>
            <AgyleDictionaryField
                size="small"
                noBorder={false}
                withNewBackground
                open={autocompleteOpen}
                onOpen={handleAutocompleteState.bind(this, true)}
                onClose={handleAutocompleteState.bind(this, false)}
                disabled={disabled || !isEmpty(value)}
                isOptionEqualToValue={(option, value) => option?.code === value?.code}
                getOptionLabel={(option) => option.display}
                options={autocompleteOptions}
                loading={autocompleteLoading}
                filterOptions={(x) => x}
                noOptionsText={!isEmpty(autocompleteInputValueDebounced) ? t('Nothing found') : t('Please enter at least three letter')}
                loadingText={t('Loading data ...')}
                onInputChange={(event, newInputValue) => setAutocompleteInputValue(newInputValue)}
                value={value}
                onChange={handleChange}
                error={error}
                disableClearable
                placeholder={t('Search for a generic drug (e.g., Paracetamol)...')}
                startIcon={
                    <Box
                        sx={{
                            pl: 1,
                            display: 'flex',
                            alignItems: 'center',
                            color: 'text.secondary',
                        }}
                    >
                        <SearchIcon />
                    </Box>
                }
                renderOption={(props, option) => (
                    <Box
                        component="li"
                        {...props}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                color: 'text.secondary',
                                display: 'flex',
                            }}
                        >
                            <MedicationIcon />
                        </Box>
                        {option.display}
                    </Box>
                )}
            />
        </div>
    );
};

export default MedicineAutocomplete;
